import { createFileRoute } from "@tanstack/react-router";
import { CheckIcon, LanguagesIcon, MonitorIcon, MoonStarIcon, SunIcon } from "lucide-react";

import { useTheme } from "@/components/theme/use-theme";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/components/ui/lib/utils";
import { Separator } from "@/components/ui/separator";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";
import { getLocale, locales, setLocale } from "@/paraglide/runtime";

export const Route = createFileRoute("/(app)/settings/system")({
	component: RouteComponent,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.systemSettings_title()} | StudyOS`,
			description: m.systemSettings_description(),
			pathname: match.pathname,
		}),
});

const themeOptions = [
	{
		value: "light",
		label: () => m.user_themeLight(),
		description: () => m.systemSettings_themeLightDescription(),
		icon: SunIcon,
	},
	{
		value: "dark",
		label: () => m.user_themeDark(),
		description: () => m.systemSettings_themeDarkDescription(),
		icon: MoonStarIcon,
	},
	{
		value: "system",
		label: () => m.user_themeSystem(),
		description: () => m.systemSettings_themeSystemDescription(),
		icon: MonitorIcon,
	},
] as const;

const localeConfig: Record<string, { emoji: string; label: string; description: string }> = {
	en: {
		emoji: "🇺🇸",
		label: "English",
		description: "English",
	},
	zh: {
		emoji: "🇨🇳",
		label: "中文",
		description: "Chinese",
	},
	es: {
		emoji: "🇪🇸",
		label: "Español",
		description: "Spanish",
	},
};

function RouteComponent() {
	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium">{m.systemSettings_title()}</h3>
				<p className="text-sm text-muted-foreground">{m.systemSettings_description()}</p>
			</div>
			<Separator />
			<div className="space-y-4">
				<ThemeSettingsCard />
				<LanguageSettingsCard />
			</div>
		</div>
	);
}

function ThemeSettingsCard() {
	const { themeMode, resolvedTheme, setTheme } = useTheme();

	return (
		<Card>
			<CardHeader className="gap-2">
				<div className="flex items-center gap-2">
					<Badge variant="secondary">{m.systemSettings_themeBadge()}</Badge>
				</div>
				<CardTitle className="flex items-center gap-2 text-base">
					<SunIcon className="size-4" />
					{m.user_theme()}
				</CardTitle>
				<p className="text-sm text-muted-foreground">
					{m.systemSettings_themeDescription()}
				</p>
			</CardHeader>
			<CardContent className="space-y-3">
				{themeOptions.map((option) => {
					const Icon = option.icon;
					const active = themeMode === option.value;
					const showResolved =
						option.value === "system" && themeMode === "system" ? resolvedTheme : null;

					return (
						<button
							key={option.value}
							type="button"
							onClick={() => setTheme(option.value)}
							className={cn(
								"flex w-full items-start justify-between rounded-xl border p-4 text-left transition-colors",
								active ? "border-primary bg-primary/5" : "hover:bg-accent/50",
							)}
						>
							<div className="flex min-w-0 items-start gap-3">
								<div
									className={cn(
										"mt-0.5 rounded-md border p-2",
										active
											? "border-primary/30 bg-background"
											: "bg-background",
									)}
								>
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<div className="flex items-center gap-2">
										<span className="font-medium">{option.label()}</span>
										{showResolved ? (
											<Badge variant="outline">
												{showResolved === "dark"
													? m.user_themeDark()
													: m.user_themeLight()}
											</Badge>
										) : null}
									</div>
									<p className="text-sm text-muted-foreground">
										{option.description()}
									</p>
								</div>
							</div>
							{active ? <CheckIcon className="mt-1 size-4 text-primary" /> : null}
						</button>
					);
				})}
			</CardContent>
		</Card>
	);
}

function LanguageSettingsCard() {
	const currentLocale = getLocale();

	return (
		<Card>
			<CardHeader className="gap-2">
				<div className="flex items-center gap-2">
					<Badge variant="secondary">{m.systemSettings_languageBadge()}</Badge>
				</div>
				<CardTitle className="flex items-center gap-2 text-base">
					<LanguagesIcon className="size-4" />
					{m.systemSettings_languageTitle()}
				</CardTitle>
				<p className="text-sm text-muted-foreground">
					{m.systemSettings_languageDescription()}
				</p>
			</CardHeader>
			<CardContent className="space-y-3">
				{locales.map((locale) => {
					const config = localeConfig[locale];
					const active = currentLocale === locale;

					return (
						<button
							key={locale}
							type="button"
							onClick={() => setLocale(locale)}
							className={cn(
								"flex w-full items-start justify-between rounded-xl border p-4 text-left transition-colors",
								active ? "border-primary bg-primary/5" : "hover:bg-accent/50",
							)}
						>
							<div className="flex min-w-0 items-start gap-3">
								<div
									className={cn(
										"mt-0.5 rounded-md border bg-background px-2 py-1 text-base",
										active ? "border-primary/30" : "",
									)}
								>
									{config?.emoji ?? "🌐"}
								</div>
								<div className="min-w-0">
									<div className="font-medium">
										{config?.label ?? locale.toUpperCase()}
									</div>
									<p className="text-sm text-muted-foreground">
										{config?.description ?? locale}
									</p>
								</div>
							</div>
							{active ? <CheckIcon className="mt-1 size-4 text-primary" /> : null}
						</button>
					);
				})}
			</CardContent>
		</Card>
	);
}
